/**
 * Typed wrappers for the Expense Tracking module (Phase 6.2). Paths and
 * shapes are taken VERBATIM from backend/internal/router/router.go and
 * the expenses.go handler / models.
 *
 * Cookie-only auth + the CSRF interceptor + 401→refresh are handled by
 * the shared axios instance (lib/api/client.ts). Every /expenses route
 * is fenced server-side by RequireFeature("expense"); the api client
 * turns the backend's 403 { error: "upgrade_required" } into a /pricing
 * redirect, and other 403s reject normally (the caller renders a
 * graceful state).
 *
 * SECURITY NOTES:
 *  - No client-supplied tenant_id / user_id is ever sent: the server
 *    derives both from the session. The expense list is role-FILTERED
 *    in SQL (employee=own, manager=own+reports, HR=all) — that SQL
 *    scope is the real boundary, NOT a query param.
 *  - There is NO single-expense GET endpoint (deliberate, like leave) —
 *    detail is derived from the role-scoped list (see use-expenses.ts).
 *  - Receipt + CSV are authed blob streams. A receipt the caller may
 *    not see (or whose blob is gone) is 404 server-side (NOT 403 — no
 *    existence oracle); the JSON envelope arrives as an UNPARSED Blob,
 *    so we rehydrate it via the shared blobErrorToApi() so the caller's
 *    toApiError() / not_found branch works. file_path (the storage key)
 *    is never on the wire.
 *  - Money is integer paise on the wire — never parsed as a float here.
 */

import { api, blobErrorToApi } from "./client";
import type {
  BatchDetailResponse,
  CreateBatchRequest,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  Expense,
  ExpenseCategory,
  ExpenseCategoryRequest,
  ExpenseDecisionRequest,
  ExpenseReceipt,
  ExpenseReceiptResponse,
  ListBatchesResponse,
  ListExpenseCategoriesResponse,
  ListExpenseReceiptsResponse,
  ListExpensesResponse,
  MarkBatchPaidRequest,
  ReimbursementBatch,
} from "@/types/expenses";

export const EXPENSE_BASE = "/api/v1/expenses";

// Reimbursement batches are their OWN top-level resource — NOT under
// /api/v1/expenses/batches. The backend relocated them (P0 fix
// 2026-05-18): Go 1.22's mux panics on /expenses/batches/{id} vs
// /expenses/{id}/receipts, crashing the server on boot. Keep this in
// lockstep with backend/internal/router/router.go (expense-batches
// block) — guarded server-side by router.TestNew_NoMuxPatternConflict.
export const EXPENSE_BATCH_BASE = "/api/v1/expense-batches";

// ---------------------------------------------------------------------------
// Categories (mutations RBAC company_admin/hr_manager server-side)
// ---------------------------------------------------------------------------

// GET /expenses/categories — non-HR sees active only; HR sees all
// (server-side).
export async function listExpenseCategories(
  signal?: AbortSignal,
): Promise<ListExpenseCategoriesResponse> {
  const { data } = await api.get<ListExpenseCategoriesResponse>(
    `${EXPENSE_BASE}/categories`,
    { signal },
  );
  return data;
}

// POST /expenses/categories — RBAC + CSRF + Audit.
export async function createExpenseCategory(
  body: ExpenseCategoryRequest,
): Promise<ExpenseCategory> {
  const { data } = await api.post<ExpenseCategory>(
    `${EXPENSE_BASE}/categories`,
    body,
  );
  return data;
}

// PATCH /expenses/categories/{id} — RBAC + CSRF + Audit. An explicit
// empty gl_code clears the GL mapping; max_amount_paise <= 0 clears the
// cap; absent fields are unchanged (server semantics).
export async function updateExpenseCategory(
  id: number,
  body: ExpenseCategoryRequest,
): Promise<ExpenseCategory> {
  const { data } = await api.patch<ExpenseCategory>(
    `${EXPENSE_BASE}/categories/${id}`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Expenses — list (role-FILTERED in SQL) / create / submit / decide / delete
// ---------------------------------------------------------------------------

// GET /expenses[?status=&from=&to=][&exclude_self=1][&mine=1] —
// employee=own, manager=own+reports, HR=tenant-all. `exclude_self=1`
// narrows an approver's list to claims they can ACT on (own claims
// removed); `mine=1` narrows to only the caller's own claims. Neither
// widens the SQL scope.
export async function listExpenses(
  params?: {
    status?: string;
    from?: string;
    to?: string;
    exclude_self?: boolean;
    mine?: boolean;
  },
  signal?: AbortSignal,
): Promise<ListExpensesResponse> {
  const { exclude_self, mine, ...rest } = params ?? {};
  const { data } = await api.get<ListExpensesResponse>(EXPENSE_BASE, {
    params: {
      ...rest,
      ...(exclude_self ? { exclude_self: 1 } : {}),
      ...(mine ? { mine: 1 } : {}),
    },
    signal,
  });
  return data;
}

// POST /expenses — own claim (server derives user/tenant). `submit`
// false saves a DRAFT (then attach receipts + POST .../submit); true
// files straight to submitted (rejected server-side for a
// requires_receipt category — no receipt yet). CSRF + Audit.
export async function createExpense(
  body: CreateExpenseRequest,
): Promise<Expense> {
  const { data } = await api.post<Expense>(EXPENSE_BASE, body);
  return data;
}

// POST /expenses/{id}/submit — claimant's OWN draft only; the server
// re-checks the cap + requires_receipt rules at submission. CSRF+Audit.
export async function submitExpense(id: number): Promise<Expense> {
  const { data } = await api.post<Expense>(
    `${EXPENSE_BASE}/${id}/submit`,
    {},
  );
  return data;
}

// POST /expenses/{id}/approve — manager-of-claimant or HR; never own
// claim (self-decision 403'd server-side). CSRF + Audit.
export async function approveExpense(
  id: number,
  body?: ExpenseDecisionRequest,
): Promise<Expense> {
  const { data } = await api.post<Expense>(
    `${EXPENSE_BASE}/${id}/approve`,
    body ?? {},
  );
  return data;
}

// POST /expenses/{id}/reject — manager-of-claimant or HR. CSRF + Audit.
export async function rejectExpense(
  id: number,
  body?: ExpenseDecisionRequest,
): Promise<Expense> {
  const { data } = await api.post<Expense>(
    `${EXPENSE_BASE}/${id}/reject`,
    body ?? {},
  );
  return data;
}

// POST /expenses/{id}/send-back — return a submitted claim to the
// claimant as a draft with a required note (fix + resubmit). Same authz
// as approve/reject; never your own claim. CSRF + Audit.
export async function sendBackExpense(
  id: number,
  body: { notes: string },
): Promise<Expense> {
  const { data } = await api.post<Expense>(
    `${EXPENSE_BASE}/${id}/send-back`,
    body,
  );
  return data;
}

// PATCH /expenses/{id} — edit the claimant's OWN DRAFT (incl. a claim
// sent back for changes). Someone else's claim → 404; not a draft → 409
// not_draft. CSRF + Audit.
export async function updateExpense(
  id: number,
  body: UpdateExpenseRequest,
): Promise<Expense> {
  const { data } = await api.patch<Expense>(`${EXPENSE_BASE}/${id}`, body);
  return data;
}

// DELETE /expenses/{id} — owner's DRAFT only (the only "cancel" an
// expense has); the server removes the row then the receipt blobs
// (orphan-tolerant). CSRF + Audit.
export async function deleteExpense(id: number): Promise<void> {
  await api.delete(`${EXPENSE_BASE}/${id}`);
}

// ---------------------------------------------------------------------------
// Receipts — multi-file upload (one per call) / list / download / delete
// ---------------------------------------------------------------------------

// GET /expenses/{id}/receipts — access-scoped server-side (only someone
// who may see the parent expense; an unauthorized id is 404, no oracle).
export async function listExpenseReceipts(
  expenseId: number,
  signal?: AbortSignal,
): Promise<ListExpenseReceiptsResponse> {
  const { data } = await api.get<ListExpenseReceiptsResponse>(
    `${EXPENSE_BASE}/${expenseId}/receipts`,
    { signal },
  );
  return data;
}

// POST /expenses/{id}/receipts — multipart, ONE file per call (caller
// loops for multi). CSRF + Audit. The CSRF-aware shared client injects
// X-CSRF-Token; we let the browser set the multipart boundary by
// passing FormData + an undefined Content-Type (axios drops its JSON
// default). The backend SNIFFS the bytes (PDF/JPEG/PNG/WebP/DOC/DOCX);
// an unsupported_type / file_too_large is surfaced by the caller.
export async function uploadExpenseReceipt(
  expenseId: number,
  file: File,
): Promise<ExpenseReceipt> {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post<ExpenseReceiptResponse>(
    `${EXPENSE_BASE}/${expenseId}/receipts`,
    fd,
    { headers: { "Content-Type": undefined } },
  );
  return data.receipt;
}

// GET /expenses/receipts/{receiptId}/download — authed blob stream.
// A receipt the caller may not see (or whose blob is gone) is 404 (NOT
// 403 — no existence oracle); rehydrate the blob-wrapped JSON envelope
// so the caller's toApiError()/not_found branch works. file_path is
// never predictable/public — the bytes only exist behind this route.
export async function downloadExpenseReceipt(
  receiptId: number,
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${EXPENSE_BASE}/receipts/${receiptId}/download`,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// DELETE /expenses/receipts/{receiptId} — claimant (own expense) or HR,
// only while the claim is mutable (draft/submitted). The server removes
// the row then the blob (orphan-tolerant). CSRF + Audit.
export async function deleteExpenseReceipt(
  receiptId: number,
): Promise<void> {
  await api.delete(`${EXPENSE_BASE}/receipts/${receiptId}`);
}

// ---------------------------------------------------------------------------
// Reimbursement batches (ALL RBAC company_admin/hr_manager server-side,
// incl. the GET routes — this is a finance surface)
// ---------------------------------------------------------------------------

// GET /expenses/batches[?status=] — RBAC HR.
export async function listBatches(
  params?: { status?: string },
  signal?: AbortSignal,
): Promise<ListBatchesResponse> {
  const { data } = await api.get<ListBatchesResponse>(
    `${EXPENSE_BATCH_BASE}`,
    { params, signal },
  );
  return data;
}

// GET /expenses/batches/{id} — RBAC HR. Returns the batch + its items.
export async function getBatch(
  id: number,
  signal?: AbortSignal,
): Promise<BatchDetailResponse> {
  const { data } = await api.get<BatchDetailResponse>(
    `${EXPENSE_BATCH_BASE}/${id}`,
    { signal },
  );
  return data;
}

// POST /expenses/batches — RBAC HR + CSRF + Audit. Built from APPROVED,
// not-yet-batched expense ids; the server validates every id is
// approved/in-tenant/un-batched in ONE tx (a single ineligible id
// rejects the whole batch → 422 ineligible_expenses).
export async function createBatch(
  body: CreateBatchRequest,
): Promise<ReimbursementBatch> {
  const { data } = await api.post<ReimbursementBatch>(
    `${EXPENSE_BATCH_BASE}`,
    body,
  );
  return data;
}

// POST /expenses/batches/{id}/processing — RBAC HR + CSRF + Audit.
// open → processing (else 409 invalid_transition).
export async function markBatchProcessing(
  id: number,
): Promise<ReimbursementBatch> {
  const { data } = await api.post<ReimbursementBatch>(
    `${EXPENSE_BATCH_BASE}/${id}/processing`,
    {},
  );
  return data;
}

// POST /expenses/batches/{id}/paid — RBAC HR + CSRF + Audit. processing
// → paid; atomically flips every member expense to reimbursed. Wave 2:
// optional payment_account_id = the wallet the batch was paid from (with
// Accounting on, the books post the payment from it; omitted → default).
export async function markBatchPaid(
  id: number,
  body: MarkBatchPaidRequest = {},
): Promise<ReimbursementBatch> {
  const { data } = await api.post<ReimbursementBatch>(
    `${EXPENSE_BATCH_BASE}/${id}/paid`,
    body,
  );
  return data;
}

// POST /expenses/batches/{id}/cancel — RBAC HR + CSRF + Audit.
// open|processing → cancelled; atomically detaches items and reverts
// member expenses to approved.
export async function cancelBatch(
  id: number,
): Promise<ReimbursementBatch> {
  const { data } = await api.post<ReimbursementBatch>(
    `${EXPENSE_BATCH_BASE}/${id}/cancel`,
    {},
  );
  return data;
}

// GET /expenses/batches/{id}/export.csv — RBAC HR. Authed CSV blob the
// payroll team consumes (money rendered integer paise → rupees
// server-side). A missing batch is 404 — rehydrate the blob-wrapped
// envelope so the caller's not_found branch works (no oracle). The
// server sets the Content-Disposition filename; we honour it.
export async function exportBatchCSV(id: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${EXPENSE_BATCH_BASE}/${id}/export.csv`,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}
